import { ArrowRightIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon } from 'core/components/Icons'
import { SamarthLogo } from 'core/components/Logo/SamarthLogo'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

interface LoginFormInputs {
  email: string
  password: string
  rememberMe: boolean
}

export const LoginPage = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })

  // Get redirect path
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const onSubmit = async (data: LoginFormInputs) => {
    setSubmitting(true)
    setErrorMessage(null)
    try {
      await login(data.email, data.password)
      toast.success('Logged in successfully')
      navigate(from, { replace: true })
    } catch (err) {
      const errorVal = err as Error
      setErrorMessage(errorVal.message || 'Authentication failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-bg text-text-primary transition-colors duration-300">
      <div className="w-full max-w-md px-4 sm:px-6 py-6">
        {/* Logo container */}
        <div className="flex justify-center mb-2 sm:mb-4">
          <SamarthLogo showText={true} size="large" layout="col" />
        </div>

        {/* Welcome Text */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-text-primary">Welcome Back</h2>
          <p className="mt-2 text-sm text-text-secondary">Sign in to access your admin portal</p>
        </div>

        {/* Form container */}
        <div className="bg-surface border border-border shadow-card rounded-card p-6 sm:p-8 transition-colors duration-300">
          {errorMessage && (
            <div className="mb-6 p-4 rounded-btn bg-danger/10 border border-danger text-danger text-sm font-medium flex items-center gap-2 animate-shake">
              <span className="flex h-2 w-2 rounded-full bg-danger animate-pulse" />
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Address */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary/60">
                  <MailIcon size={18} />
                </span>
                <input
                  id="email"
                  type="text"
                  placeholder="Enter your email address"
                  className={`w-full rounded-btn border bg-bg py-3 pl-10 pr-4 text-sm text-text-primary placeholder-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                    errors.email ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                  }`}
                  {...register('email', {
                    required: 'Email address is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address format',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <span className="mt-1 block text-xs text-danger font-semibold">{errors.email.message}</span>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-text-secondary">
                  Password
                </label>
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary/60">
                  <LockIcon size={18} />
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className={`w-full rounded-btn border bg-bg py-3 pl-10 pr-10 text-sm text-text-primary placeholder-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                    errors.password ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                  }`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-secondary/60 hover:text-text-primary"
                >
                  {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
              {errors.password && (
                <span className="mt-1 block text-xs text-danger font-semibold">{errors.password.message}</span>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20 bg-bg accent-primary transition-colors cursor-pointer"
                  {...register('rememberMe')}
                />
                <span className="ml-2 text-xs font-semibold text-text-secondary">Remember me</span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary hover:brightness-110 active:brightness-95 text-primary-fg font-bold py-3 px-4 rounded-btn flex items-center justify-center gap-2 shadow-soft hover:shadow-md transition-all duration-200 disabled:opacity-50"
            >
              {submitting ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-fg border-t-transparent" />
              ) : (
                <>
                  Sign In
                  <ArrowRightIcon size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Legal Links Footer */}
        <div className="mt-8 text-center text-xs text-text-secondary flex items-center justify-center gap-4">
          <Link
            to="/privacy-policy"
            className="hover:text-primary transition-colors underline-offset-4 hover:underline"
          >
            Privacy Policy
          </Link>
          <span>•</span>
          <Link
            to="/terms-of-service"
            className="hover:text-primary transition-colors underline-offset-4 hover:underline"
          >
            Terms of Service
          </Link>
          <span>•</span>
          <a
            href="https://samarthagri.com"
            target="_blank"
            rel="noreferrer"
            className="hover:text-primary transition-colors underline-offset-4 hover:underline"
          >
            samarthagri.com
          </a>
        </div>
      </div>
    </div>
  )
}
